import React, { useEffect, useState } from "react";
import { Box, Chip, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useRecoilValue } from "recoil";
import IncidentVideosTab from "../../Components/Common/IncidentVideosTab";
import CameraIncidentsChart from "../../Components/Common/CameraIncidentsChart";
import ModelAuditChart from "../../Components/Common/ModelAuditChart";
import FoundItemsGallery from "../../Components/LostFound/FoundItemsGallery";
import LostReportsTab from "../../Components/LostFound/LostReportsTab";
import LostFoundAnalytics from "../../Components/LostFound/LostFoundAnalytics";
import LostFoundEventFilter from "../../Components/LostFound/LostFoundEventFilter";
import { EVENT_STATUS_OPTIONS } from "../../Components/LostFound/lostFoundConstants";
import { authState } from "../../Recoil/RecoilState";
import { baseURL } from "../../utils/StaticVariables";
import {
  fetchLostFoundEventStats,
  lostFoundEventsEndpoint,
  lostFoundResolveEndpoint,
} from "../../services/lostFoundApi";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`lost-found-tabpanel-${index}`}
      aria-labelledby={`lost-found-tab-${index}`}
      style={{ minHeight: "calc(100vh - 200px)" }}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3, animation: "fadeIn 0.5s ease-in-out" }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `lost-found-tab-${index}`,
    "aria-controls": `lost-found-tabpanel-${index}`,
  };
}

const LostFoundVisualizations = () => {
  const [selectedChart, setSelectedChart] = useState("overview");

  return (
    <Box sx={{ p: 1, display: "flex", flexDirection: "column", width: "100%" }}>
      <Box sx={{ maxWidth: 400, mb: 4 }}>
        <FormControl fullWidth>
          <InputLabel id="lost-found-vis-select-label">Select Visualization</InputLabel>
          <Select
            labelId="lost-found-vis-select-label"
            value={selectedChart}
            label="Select Visualization"
            onChange={(e) => setSelectedChart(e.target.value)}
          >
            <MenuItem value="overview">Overview & Reunions</MenuItem>
            <MenuItem value="incidents_per_camera">Number of Incidents per Camera</MenuItem>
            <MenuItem value="model_audit">Model Accuracy & Confidence Audit</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ animation: "fadeIn 0.5s ease-in-out", width: "100%" }}>
        {selectedChart === "overview" ? (
          <LostFoundAnalytics />
        ) : selectedChart === "incidents_per_camera" ? (
          <CameraIncidentsChart
            featureTitle="Lost & Found"
            apiEndpoint={`${baseURL}lost-found/analytics/incidents-per-camera`}
          />
        ) : (
          <ModelAuditChart
            featureTitle="Lost & Found"
            apiEndpoint={`${baseURL}lost-found/analytics/confidence-audit`}
          />
        )}
      </Box>
    </Box>
  );
};

const LostFoundMain = () => {
  const [value, setValue] = useState(0);
  const [stats, setStats] = useState(null);
  const { token } = useRecoilValue(authState);

  useEffect(() => {
    if (!token) return;
    fetchLostFoundEventStats(token)
      .then(setStats)
      .catch(() => setStats(null));
  }, [token, value]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }} flexWrap="wrap">
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
          Lost & Found
        </Typography>
        {stats && (
          <>
            <Chip color={stats.open ? "error" : "default"} label={`${stats.open} open alerts`} />
            <Chip variant="outlined" label={`${stats.last_24h} in the last 24 h`} />
          </>
        )}
      </Stack>

      <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "background.paper" }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="Lost & Found dashboard tabs"
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            indicatorColor="primary"
            sx={{
              "& .MuiTab-root": {
                fontWeight: 600,
                fontSize: "1rem",
                textTransform: "none",
                py: 2.5,
                flexGrow: 1,
              },
            }}
          >
            <Tab label="Abandoned Events" {...a11yProps(0)} />
            <Tab label="Found Items" {...a11yProps(1)} />
            <Tab label="Lost Reports" {...a11yProps(2)} />
            <Tab label="Visualizations" {...a11yProps(3)} />
          </Tabs>
        </Box>

        <Box sx={{ bgcolor: "background.default" }}>
          <CustomTabPanel value={value} index={0}>
            <IncidentVideosTab
              featureName="Lost & Found"
              featureKey="lost_found"
              apiEndpoint={lostFoundEventsEndpoint()}
              resolveEndpoint={lostFoundResolveEndpoint()}
              statusOptions={EVENT_STATUS_OPTIONS}
              FilterComponent={LostFoundEventFilter}
            />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            <FoundItemsGallery />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={2}>
            <LostReportsTab />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={3}>
            <LostFoundVisualizations />
          </CustomTabPanel>
        </Box>
      </Paper>

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
    </Box>
  );
};

export default LostFoundMain;
